import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { jwtDecode } from 'jwt-decode';

// Начальное состояние
const initialState = {
  user: null,
  token: null,
  isAuthenticated: false,
  loading: true,
  modules: [],
  modulesLoading: false
};

// Типы действий
const AUTH_ACTIONS = {
  LOGIN: 'LOGIN',
  LOGOUT: 'LOGOUT',
  SET_LOADING: 'SET_LOADING',
  UPDATE_USER: 'UPDATE_USER',
  SET_MODULES: 'SET_MODULES',
  SET_MODULES_LOADING: 'SET_MODULES_LOADING'
};

// Роли и их права
export const ROLES = {
  GUEST: 'guest',
  USER: 'user',
  ADMIN: 'admin'
};

export const PERMISSIONS = {
  READ: 'read',
  WRITE: 'write',
  DELETE: 'delete',
  MANAGE: 'manage',
  OWN: 'own'
};

// Права для каждой роли
export const ROLE_PERMISSIONS = {
  [ROLES.GUEST]: [PERMISSIONS.READ],
  [ROLES.USER]: [PERMISSIONS.READ, PERMISSIONS.WRITE, PERMISSIONS.OWN],
  [ROLES.ADMIN]: [PERMISSIONS.READ, PERMISSIONS.WRITE, PERMISSIONS.DELETE, PERMISSIONS.MANAGE, PERMISSIONS.OWN]
};

// Редьюсер
const authReducer = (state, action) => {
  console.log('AuthContext: reducer - action:', action.type, 'payload:', action.payload);
  console.log('AuthContext: reducer - текущее состояние:', state);
  
  switch (action.type) {
    case AUTH_ACTIONS.LOGIN:
      const newState = {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        isAuthenticated: true,
        loading: false
      };
      console.log('AuthContext: reducer - новое состояние после LOGIN:', newState);
      return newState;
    
    case AUTH_ACTIONS.LOGOUT:
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        loading: false
      };
    
    case AUTH_ACTIONS.SET_LOADING:
      return {
        ...state,
        loading: action.payload
      };
    
    case AUTH_ACTIONS.UPDATE_USER:
      return {
        ...state,
        user: { ...state.user, ...action.payload }
      };
    
    case AUTH_ACTIONS.SET_MODULES:
      return {
        ...state,
        modules: action.payload,
        modulesLoading: false
      };
    
    case AUTH_ACTIONS.SET_MODULES_LOADING:
      return {
        ...state,
        modulesLoading: action.payload
      };
    
    default:
      return state;
  }
};

// Создание контекста
const AuthContext = createContext();

// Хук для использования контекста
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth должен использоваться внутри AuthProvider');
  }
  return context;
};

// Провайдер контекста
export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Проверка токена при загрузке
  useEffect(() => {
    const initAuth = async () => {
      const token = localStorage.getItem('token');
      const userData = localStorage.getItem('user');
      
      console.log('AuthContext: initAuth - token:', !!token, 'userData:', !!userData);
      
      if (token && userData) {
        try {
          const decoded = jwtDecode(token);
          const currentTime = Date.now() / 1000;
          
          if (decoded.exp > currentTime) {
            const user = JSON.parse(userData);
            console.log('AuthContext: пользователь загружен из localStorage:', user);
            
            // Загружаем актуальный профиль с сервера
            try {
              const response = await fetch('https://limitverify.ru:5000/api/auth/profile', {
                headers: {
                  'Authorization': `Bearer ${token}`
                }
              });
              
              if (response.ok) {
                const profileData = await response.json();
                const updatedUser = profileData.user;
                console.log('AuthContext: актуальный профиль загружен:', updatedUser);
                
                // Обновляем localStorage с актуальными данными
                localStorage.setItem('user', JSON.stringify(updatedUser));
                
                dispatch({
                  type: AUTH_ACTIONS.LOGIN,
                  payload: { user: updatedUser, token }
                });
              } else {
                // Если не удалось загрузить профиль, используем данные из localStorage
                console.log('AuthContext: не удалось загрузить профиль, используем localStorage');
                dispatch({
                  type: AUTH_ACTIONS.LOGIN,
                  payload: { user, token }
                });
              }
            } catch (profileError) {
              console.error('AuthContext: ошибка загрузки профиля:', profileError);
              // Используем данные из localStorage
              dispatch({
                type: AUTH_ACTIONS.LOGIN,
                payload: { user, token }
              });
            }
            
            console.log('AuthContext: dispatch LOGIN выполнен');
            
            // Загружаем модули пользователя
            await loadUserModules();
          } else {
            console.log('AuthContext: токен истек');
            // Токен истек, очищаем localStorage
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
          }
        } catch (error) {
          console.error('AuthContext: ошибка декодирования токена:', error);
          // Очищаем поврежденные данные
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
        }
      } else {
        console.log('AuthContext: нет токена или данных пользователя');
        dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
      }
    };

    initAuth();
  }, []);

  // Функция входа
  const login = async (email, password) => {
    try {
      console.log('AuthContext: начинаем вход для', email);
      dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: true });
      
      const response = await fetch('https://limitverify.ru:5000/api/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      if (!response.ok) {
        throw new Error('Ошибка авторизации');
      }

      const data = await response.json();
      console.log('AuthContext: получен ответ от бекенда:', data);
      
      // Сохранение токена
      localStorage.setItem('token', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));
      
      console.log('AuthContext: сохраняем в localStorage:', { token: data.token, user: data.user });
      
      dispatch({
        type: AUTH_ACTIONS.LOGIN,
        payload: {
          user: data.user, // Используем полные данные от бекенда
          token: data.token
        }
      });

      console.log('AuthContext: dispatch LOGIN выполнен');
      
      // Загружаем модули пользователя
      await loadUserModules();
      
      return { success: true };
    } catch (error) {
      console.error('AuthContext: ошибка входа:', error);
      dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
      return { success: false, error: error.message };
    }
  };

  // Функция выхода
  const logout = () => {
    localStorage.removeItem('token');
    dispatch({ type: AUTH_ACTIONS.LOGOUT });
  };

  // Функция регистрации
  const register = async (userData) => {
    try {
      dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: true });
      
      const response = await fetch('https://limitverify.ru:5000/api/auth/register', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(userData),
      });

      if (!response.ok) {
        throw new Error('Ошибка регистрации');
      }

      const data = await response.json();
      
      // Автоматический вход после регистрации
      if (data.token) {
        localStorage.setItem('token', data.token);
        localStorage.setItem('user', JSON.stringify(data.user));
        
        dispatch({
          type: AUTH_ACTIONS.LOGIN,
          payload: {
            user: data.user, // Используем полные данные от бекенда
            token: data.token
          }
        });
      }

      return { success: true };
    } catch (error) {
      dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
      return { success: false, error: error.message };
    }
  };

  // Загрузка модулей пользователя
  const loadUserModules = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      console.log('AuthContext: загружаем модули пользователя...');
      console.log('AuthContext: ID пользователя:', state.user?.id);
      
      // Устанавливаем состояние загрузки модулей
      dispatch({ type: AUTH_ACTIONS.SET_MODULES_LOADING, payload: true });
      
      const response = await fetch('https://limitverify.ru:5000/api/modules/user-modules', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      console.log('AuthContext: ответ от API модулей:', response.status);

      if (response.ok) {
        const modules = await response.json();
        console.log('AuthContext: получены модули:', modules);
        dispatch({ type: AUTH_ACTIONS.SET_MODULES, payload: modules });
      } else {
        console.error('AuthContext: ошибка загрузки модулей:', response.status, response.statusText);
        dispatch({ type: AUTH_ACTIONS.SET_MODULES_LOADING, payload: false });
      }
    } catch (error) {
      console.error('Error loading user modules:', error);
      dispatch({ type: AUTH_ACTIONS.SET_MODULES_LOADING, payload: false });
    }
  };

  // Проверка доступа к модулю
  const hasModuleAccess = (moduleName) => {
    console.log('AuthContext: проверяем доступ к модулю:', moduleName);
    console.log('AuthContext: текущие модули:', state.modules);
    console.log('AuthContext: пользователь:', state.user);
    
    if (!state.user || !state.modules) {
      console.log('AuthContext: нет пользователя или модулей');
      return false;
    }
    
    const hasAccess = state.modules.some(m => m.name === moduleName);
    console.log('AuthContext: доступ к модулю', moduleName, ':', hasAccess);
    return hasAccess;
  };

  // Проверка роли
  const hasRole = (role) => {
    if (!state.user) return false;
    return state.user.role === role;
  };

  // Проверка минимальной роли
  const hasMinRole = (minRole) => {
    if (!state.user) return false;
    
    const roleHierarchy = {
      [ROLES.GUEST]: 0,
      [ROLES.USER]: 1,
      [ROLES.ADMIN]: 2
    };
    
    return roleHierarchy[state.user.role] >= roleHierarchy[minRole];
  };

  // Обновление данных пользователя
  const updateUser = (userData) => {
    const updatedUser = { ...state.user, ...userData };
    localStorage.setItem('user', JSON.stringify(updatedUser));
    dispatch({
      type: AUTH_ACTIONS.UPDATE_USER,
      payload: userData
    });
  };

  const value = {
    ...state,
    login,
    logout,
    register,
    loadUserModules,
    hasModuleAccess,
    hasRole,
    hasMinRole,
    updateUser
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
